import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, ZapIcon, HeadphonesIcon } from 'lucide-react';
import { Container } from '../components/Container';
const pillars = [{
  icon: ShieldCheckIcon,
  eyebrow: '99.9% uptime',
  title: 'Reliable and stable',
  desc: 'Pigeon offers enterprise-grade reliability, backed by a 99.9% uptime guarantee.'
}, {
  icon: ZapIcon,
  eyebrow: 'Rapid setup',
  title: 'Onboarding and implementation',
  desc: 'Get up and running fast. We help set up your workflows, train your team, and provide helpful resources.'
}, {
  icon: HeadphonesIcon,
  eyebrow: '24/7 support',
  title: 'Customer and technical support',
  desc: 'White-glove technical service and 24/7 dedicated customer support, with an average response time of under 20 minutes.'
}];
const certs = [{
  name: 'SOC 2 Type II',
  desc: 'Annual third-party audit of security, availability, and privacy controls.',
  logo: 'https://www.pigeonimmigration.com/_assets/v11/9a444ee7f8b58e2bc9449b329edc71e81703f7be.png'
}, {
  name: 'GDPR',
  desc: 'Privacy-by-design platform compliant with EU data rights.',
  logo: 'https://www.pigeonimmigration.com/_assets/v11/617aa0997cd332dd60d38c152aba0ecfdf84863e.png'
}, {
  name: 'HIPAA',
  desc: 'End-to-end protection for U.S. healthcare data (PHI).',
  logo: 'https://www.pigeonimmigration.com/_assets/v11/6c32fadc695ec83f70310bec49aea03825f769ba.png'
}];
export function SecurityCompliance() {
  return <section id="security" className="relative border-t border-white/5 bg-zinc-950 py-12 sm:py-16" data-id="element-842">
      <Container data-id="element-843">
        {/* Header — technical, authoritative */}
        <div className="mx-auto mb-10 max-w-3xl text-center" data-id="element-844">
          <h2 className="font-mono text-[2.5rem] font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-3xl lg:text-4xl" data-id="element-845">
            Enterprise-grade
            <br data-id="element-846" />
            <span className="text-accent" data-id="element-847">security &amp; compliance.</span>
          </h2>
        </div>

        {/* MOBILE — compact list rows: the eyebrow stat is the punchy fact, title is short, desc dropped */}
        <ul className="divide-y divide-white/[0.06] overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-900/30 sm:hidden" data-id="element-848">
          {pillars.map((p, i) => <motion.li key={p.title} initial={{
          opacity: 0,
          y: 8
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '400px'
        }} transition={{
          duration: 0.35,
          delay: i * 0.04,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex items-center gap-3 px-4 py-3.5" data-id="element-849">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-accent/20 bg-accent/10" data-id="element-850">
                <p.icon className="h-4 w-4 text-accent" data-id="element-851" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-852">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent" data-id="element-853">
                  {p.eyebrow}
                </div>
                <div className="mt-0.5 truncate text-[15px] font-semibold tracking-tight text-white" data-id="element-854">
                  {p.title}
                </div>
              </div>
            </motion.li>)}
        </ul>

        {/* DESKTOP — full pillar cards */}
        <ul className="hidden gap-3 sm:grid sm:grid-cols-3" data-id="element-855">
          {pillars.map((p, i) => <motion.li key={p.title} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '400px'
        }} transition={{
          duration: 0.4,
          delay: i * 0.05,
          ease: [0.16, 1, 0.3, 1]
        }} className="group relative overflow-hidden rounded-xl border border-white/10 bg-zinc-900/40 p-5 backdrop-blur transition-colors hover:border-accent/30" data-id="element-856">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-accent/20 bg-accent/10" data-id="element-857">
                <p.icon className="h-4 w-4 text-accent" data-id="element-858" />
              </div>
              <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-accent" data-id="element-859">
                {p.eyebrow}
              </div>
              <h3 className="mt-2 text-base font-semibold tracking-tight text-white" data-id="element-860">
                {p.title}
              </h3>
              <p className="mt-1.5 text-xs leading-relaxed text-zinc-400" data-id="element-861">
                {p.desc}
              </p>
            </motion.li>)}
        </ul>

        {/* Compliance certifications — logo-forward, bigger */}
        <div className="mt-12" data-id="element-862">
          <div className="mb-6 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500" data-id="element-863">
            Compliance certifications
          </div>

          {/* MOBILE — single trust strip: logo + name only, three across */}
          <ul className="grid grid-cols-3 gap-2 rounded-2xl border border-white/[0.08] bg-zinc-900/30 p-3 sm:hidden" data-id="element-864">
            {certs.map((c, i) => <motion.li key={c.name} initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '400px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="flex flex-col items-center gap-1.5 px-1 py-2" data-id="element-865">
                <div className="flex h-12 w-12 items-center justify-center" data-id="element-866">
                  <img src={c.logo} alt={c.name} className="max-h-full max-w-full object-contain" data-id="element-867" />
                </div>
                <div className="text-center font-mono text-[11px] font-bold uppercase tracking-[0.1em] leading-tight text-white" data-id="element-868">
                  {c.name}
                </div>
              </motion.li>)}
          </ul>

          {/* DESKTOP — full cert cards with descriptions */}
          <ul className="hidden gap-4 sm:grid sm:grid-cols-3" data-id="element-869">
            {certs.map((c, i) => <motion.li key={c.name} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '400px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.06,
            ease: [0.16, 1, 0.3, 1]
          }} className="group relative flex flex-col items-center overflow-hidden rounded-2xl border border-white/10 bg-zinc-900/40 p-5 text-center backdrop-blur sm:p-7" data-id="element-870">
                <div aria-hidden className="pointer-events-none absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-accent/[0.08] blur-3xl" data-id="element-871" />
                <div className="relative flex h-16 w-16 items-center justify-center sm:h-28 sm:w-28" data-id="element-872">
                  <img src={c.logo} alt={c.name} className="max-h-full max-w-full object-contain" data-id="element-873" />
                </div>
                <div className="mt-3 font-mono text-sm font-bold uppercase tracking-[0.12em] text-white sm:mt-5" data-id="element-874">
                  {c.name}
                </div>
                <p className="mt-1.5 max-w-[18rem] text-xs leading-relaxed text-zinc-400 sm:mt-2" data-id="element-875">
                  {c.desc}
                </p>
              </motion.li>)}
          </ul>
        </div>
      </Container>
    </section>;
}